import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Checkbox, FormControlLabel, FormGroup, FormLabel } from '@mui/material';

// Behavior options for the new step
const BEHAVIOR_OPTIONS = [
  "Greet users warmly and professionally, using their name if available to create a personal connection.",
  "Acknowledge and restate the user’s question or concern to confirm understanding before answering.",
  "Draw answers directly from the approved knowledge base or FAQs to ensure accuracy and consistency.",
  "Communicate clearly and concisely, avoiding jargon unless appropriate for the user’s technical level.",
  "Stay empathetic and patient, especially when users are confused, frustrated, or repeating questions.",
  "Offer step-by-step guidance when explaining processes, and verify the user’s understanding before moving on.",
  "Escalate immediately if a question cannot be answered confidently, or if it involves sensitive, account-specific, or urgent matters.",
  "Document or summarize key points from the conversation when escalating or closing a case to aid follow-up.",
  "Close each interaction positively, confirming that the issue is resolved or next steps are clear.",
  "Maintain brand-aligned tone and professionalism, adapting formality to match the user’s needs and the company’s style."
];

// Behavior step UI
function BehaviorScreen({ selectedBehaviors, selectAllBehaviors, onBehaviorChange, onSelectAllBehaviors }) {
  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">Select Agent Behaviors</h2>
      <FormLabel component="legend">Choose behaviors for your agent:</FormLabel>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={selectAllBehaviors} onChange={onSelectAllBehaviors} name="selectAll" />}
          label="Select All"
        />
        {BEHAVIOR_OPTIONS.map(option => (
          <FormControlLabel
            key={option}
            control={<Checkbox checked={selectedBehaviors.includes(option)} onChange={e => onBehaviorChange(option, e.target.checked)} name={option} />}
            label={option}
          />
        ))}
      </FormGroup>
    </section>
  );
}
/**
 * Chattitude Agent Instruction Builder — React Prototype (v1.0)
 * Single-file React app designed for ChatGPT canvas preview.
 * - Routes (hash-based): /builder, /builder/industry, /builder/role, /builder/tone,
 *   /builder/objectives, /builder/review
 * - Live right-panel preview (150ms debounce)
 * - Auto-save: localStorage + mocked server PATCH every 5s (and on blur)
 * - Industry branching aligned to ICPs (SaaS "Jessica", MSP "Chris", Manufacturing "Jennifer", Healthcare "Sarah", and EdTech)
 * - Accessible: keyboard focus, aria-live on preview, labeled controls
 * - Brand: Styles use Chattitude tokens per guide
 */

/****************************
 * Brand & Theme (CSS Vars)
 ****************************/
const BrandVars = () => (
  <style>{`
    :root{
      --brand-white:#FFFFFF; --brand-blue:#1976d2; --brand-orange:#FE3203; --brand-gray-500:#939393; --brand-gray-900:#202524;
      --builder-teal:#1976d2; --builder-coral:#1565c0; --builder-bg:#F5F7F9;
      --radius:8px; --shadow:0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12);
      --font-title:'Autowide', system-ui, sans-serif;
      --font-heading:'Open Sans', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      --font-body:'Roboto', ui-sans-serif, system-ui, -apple-system, Segoe UI, Arial, sans-serif;
    }
    *{box-sizing:border-box}
    body{background:var(--builder-bg)}
    .builder-root{background:var(--builder-bg);min-height:100vh;color:var(--brand-gray-900);}
    .builder-container{width:100%;margin:0;padding:5px 10px 5px;}
    .builder-grid{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,0.8fr);gap:32px;margin-top:16px;align-items:flex-start;}
    @media (max-width:1140px){.builder-grid{grid-template-columns:1fr;gap:24px;}}
    .builder-card{background:#fff;border-radius:var(--radius);box-shadow:var(--shadow);padding:24px;}
    .builder-preview-card{padding:0;}
    .builder-card.compact{padding:16px;}
    .builder-preview-card{display:flex;flex-direction:column;}
    .preview-heading{font-size:0.85rem;font-weight:600;color:var(--brand-gray-900);letter-spacing:0.08em;text-transform:uppercase;font-family:var(--font-heading);margin:0;padding:8px 12px 0;}
    .preview-subtext{font-size:0.7rem;color:var(--brand-gray-500);}
  .preview-box{margin-top:0;padding:16px;background:#0f172a;color:#e2e8f0;border-radius:12px;font-size:0.9rem;line-height:1.5;white-space:pre-wrap;min-height:280px;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;text-align:left;}
    .builder-footer{margin-top:24px;display:flex;justify-content:space-between;align-items:center;gap:16px;position:sticky;bottom:16px;background:rgba(255,255,255,0.92);padding:12px 20px;border-radius:20px;box-shadow:0 10px 30px rgba(15,23,42,0.12);backdrop-filter:blur(12px);}
    @media (max-width:900px){.builder-footer{position:static;}}
    .builder-footer .btn{min-width:130px;justify-content:center;}
    .btn{display:inline-flex;align-items:center;gap:.5rem;border-radius:16px;padding:.65rem 1rem;font-weight:600}
    .btn-primary{background:var(--brand-blue);color:#fff}
    .btn-primary:disabled{opacity:.4}
    .btn-secondary{background:#fff;border:1px solid rgba(25,118,210,0.35);color:var(--brand-gray-900)}
    .btn-secondary:hover{border-color:var(--brand-blue);}
    .btn-ghost{background:transparent;color:var(--brand-gray-900)}
    .focus-ring:focus{outline:3px solid color-mix(in srgb, var(--brand-blue) 50%, #fff);outline-offset:2px}
    .pill{border:1px solid #E6EDF2;padding:.25rem .65rem;border-radius:999px;font-size:.75rem;font-weight:600;color:var(--brand-gray-500);}
    .tone-tile{border:1px solid #E6EDF2;border-radius:12px;padding:16px;cursor:pointer;background:#fff;transition:border-color .2s ease,box-shadow .2s ease;}
    .tone-tile[data-active="true"], .tone-tile.is-selected{border-color:var(--brand-blue);box-shadow:0 0 0 3px color-mix(in srgb, var(--brand-blue) 20%, #fff);}
    .tone-tile:hover{border-color:var(--brand-blue);}
    .tone-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px;}
    .tile-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;}
    .objectives-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;}
    .objectives-grid .tone-tile{padding:14px;font-weight:500;}
    .builder-section-title{font-family:var(--font-heading);font-size:1.25rem;margin:0px 0 4px 0;}
    .builder-field{margin-bottom:16px;}
    .builder-field label{display:block;font-size:0.85rem;font-weight:600;margin-bottom:6px;color:var(--brand-gray-900);}
    .builder-text-input,.builder-select,.builder-textarea{width:100%;padding:12px 14px;border:1px solid #d0d7de;border-radius:10px;font-size:0.95rem;font-family:var(--font-body);transition:border .2s ease,box-shadow .2s ease;}
    .builder-text-input:focus,.builder-select:focus,.builder-textarea:focus{outline:none;border-color:var(--builder-teal);box-shadow:0 0 0 3px color-mix(in srgb, var(--builder-teal) 20%, #fff);}
    .builder-textarea{min-height:300px;resize:vertical;}
    .builder-actions{display:flex;gap:12px;margin-top:12px;flex-wrap:wrap;}
    .builder-summary{margin-top:12px;font-size:0.95rem;}
    .builder-summary ul{margin:12px 0 0;padding:0;list-style:none;}
    .builder-summary li{margin-bottom:6px;}
    .builder-summary strong{font-weight:600;}
    .progress-card{margin-top:0;}
    .progress-labels{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;font-size:0.8rem;font-family:var(--font-heading);}
    .progress-chips{display:flex;flex-wrap:wrap;gap:8px;}
    .progress-step{padding:4px 10px;border-radius:999px;border:1px solid #E6EDF2;font-weight:600;font-size:0.7rem;color:var(--brand-gray-500);background:#fff;}
    .progress-step.is-active{background:var(--brand-blue);border-color:var(--brand-blue);color:#fff;}
    .progress-bar{height:8px;background:#E6EDF2;border-radius:999px;overflow:hidden;}
    .progress-bar-fill{height:100%;background:var(--brand-blue);transition:width 0.2s ease;}
    .welcome-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:24px;align-items:center;}
    .welcome-card{display:flex;align-items:center;gap:16px;padding:20px;border-radius:16px;background:#fff;box-shadow:var(--shadow);}
    .welcome-card-icon{width:48px;height:48px;border-radius:18px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,var(--brand-blue),var(--builder-coral));}
    .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
    .fade{animation:fade .22s ease}
    @keyframes fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
  `}</style>
);

/****************************
 * Types & Constants
 ****************************/
const STEPS = [
  { id: 1, key: "industry", name: "Industry", path: "/builder/industry" },
  { id: 2, key: "tone", name: "Personality & Tone", path: "/builder/tone" },
  { id: 3, key: "behavior", name: "Behavior", path: "/builder/behavior" },
  { id: 4, key: "role", name: "Role", path: "/builder/role" },
  { id: 5, key: "objectives", name: "Core Objectives", path: "/builder/objectives" },
  { id: 6, key: "review", name: "Review & Generate", path: "/builder/review" },
];

const INDUSTRIES = [
  { value: "msp", label: "IT / Managed Services (MSP)" },
  { value: "saas", label: "SaaS / Software" },
  { value: "mfg", label: "Manufacturing & Distribution" },
  { value: "health", label: "Medical / Healthcare Supply" },
  { value: "edtech", label: "Education / EdTech" },
  { value: "internal", label: "Internal Operations / HR / Finance" },
  { value: "basic-support", label: "Basic Customer Support Agent" },
  { value: "other", label: "General / Other / My industry isn't listed" },
];

const DEFAULT_ROLE_DESCRIPTIONS = {
  "basic-support": "You are a helpful customer support assistant trained on this company’s knowledge base. Use that knowledge to answer customer questions clearly and concisely. If a question falls outside your knowledge or requires human assistance, guide the user to submit a form or connect with live support.",
  msp: "You are a technical support assistant for an IT and managed services provider. Use the company’s documentation to troubleshoot issues, explain service offerings, and guide users through solutions.",
  saas: "You are a support assistant for a SaaS or software platform. Use the knowledge base to explain product features, account setup, and troubleshooting steps.",
  mfg: "You are a support assistant for a manufacturing and distribution company. Use the knowledge base to answer questions about products, specifications, availability, and order processes.",
  health: "You are a knowledgeable assistant for a medical and healthcare supply organization. Provide accurate information from approved knowledge sources about products, ordering, compliance, and shipping.",
  edtech: "You are an educational support assistant helping students, educators, and administrators. Use the knowledge base to explain programs, platform features, enrollment, or troubleshooting steps.",
  internal: "You are an internal support assistant for HR, finance, and operations. Use internal documentation to answer policy, payroll, benefits, and procedure questions.",
  other: "You are a general business support assistant. Use the provided knowledge base to answer customer questions, explain company processes, and guide them toward helpful resources."
};

const DEFAULT_ROLE_TITLES = {
  "basic-support": "Customer Support Assistant",
  msp: "IT & MSP Support Assistant",
  saas: "SaaS Support Assistant",
  mfg: "Manufacturing Support Assistant",
  health: "Healthcare Supply Assistant",
  edtech: "Education Support Assistant",
  internal: "Internal Operations Assistant",
  other: "Business Support Assistant"
};

const DEFAULT_OBJECTIVES = {
  "basic-support": "Resolve customer questions accurately and efficiently while maintaining a friendly and professional experience.",
  msp: "Provide accurate technical guidance, resolve common IT issues, and escalate complex cases for human intervention.",
  saas: "Help users understand and successfully use the product while minimizing the need for human support.",
  mfg: "Ensure customers receive accurate product and order information to facilitate smooth purchasing and fulfillment.",
  health: "Deliver trustworthy, compliant information to support healthcare professionals and maintain patient safety.",
  edtech: "Help users learn, navigate, and succeed using educational programs and technology tools effectively.",
  internal: "Assist employees in understanding and following internal processes while protecting confidentiality and compliance.",
  other: "Provide clear, helpful, and accurate responses that make customers feel supported and confident in their next steps."
};



const INTEGRATIONS_BY_INDUSTRY = {
  saas: ["HubSpot", "Salesforce", "Zendesk", "Intercom", "Google Calendar"],
  msp: ["ConnectWise", "ServiceNow", "Kaseya", "Zendesk"],
  mfg: ["Salesforce", "NetSuite", "Epicor"],
  health: ["NetSuite", "Zendesk", "SAP Business One"],
  edtech: ["HubSpot", "Slate", "LMS"],
  internal: ["Google Workspace", "Microsoft 365", "Jira/Confluence"],
  "basic-support": ["Zendesk", "Freshdesk", "Intercom"],
  other: ["HubSpot", "Google Calendar", "Zendesk"],
}

/****************************
 * Helpers
 ****************************/
const KEY_TO_INDEX = Object.fromEntries(STEPS.map((s, i) => [s.key, i]));

// Use internal state instead of hash routing to avoid conflicts with React Router
function useStepNavigation(defaultStepKey = 'industry') {
  const [currentStepKey, setCurrentStepKey] = useState(defaultStepKey);

  const pushByKey = (stepKey) => {
    setCurrentStepKey(stepKey);
  };

  return { currentStepKey, pushByKey };
}

const debounce = (fn, ms = 150) => {
  let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

function cx(...classes) { return classes.filter(Boolean).join(' ') }

/****************************
 * Draft State
 ****************************/
const SAFETY_GUARDRAIL_OPTIONS = [
  {
    id: 'prohibited-topics',
    title: 'Prohibited Topics',
    description: 'Do not provide instructions for any illegal activities and avoid discussions about politics or religion.'
  },
  {
    id: 'harmful-content',
    title: 'Harmful Content Refusal',
    description: 'Refuse requests for hate speech, self-harm directions, sexually explicit content, or violence.'
  },
  {
    id: 'bias-mitigation',
    title: 'Bias Mitigation',
    description: 'Ensure responses are neutral, fair, and do not perpetuate stereotypes or biased assumptions.'
  },
  {
    id: 'pii-prevention',
    title: 'PII Prevention',
    description: 'Do not disclose or process sensitive information such as social security numbers, credit card details, or health data.'
  }
];

const RESPONSE_GUARDRAIL_OPTIONS = [
  {
    id: 'on-topic',
    title: 'On-Topic Adherence',
    description: 'Stay strictly on-topic and refuse off-topic queries.'
  },
  {
    id: 'factual-grounding',
    title: 'Factual Grounding',
    description: 'Only use information from the provided context and cite sources or references when possible to avoid hallucinations.'
  },
  {
    id: 'tone-style',
    title: 'Tone and Style Consistency',
    description: 'Follow the requested tone and writing style for every response.'
  },
  {
    id: 'conciseness',
    title: 'Conciseness and Length',
    description: 'Keep responses within the requested length (e.g., under 100 words) while remaining clear and helpful.'
  }
];

const ALL_GUARDRAIL_OPTION_IDS = [...SAFETY_GUARDRAIL_OPTIONS, ...RESPONSE_GUARDRAIL_OPTIONS].map(opt => opt.id);

const defaultDraft = {
  id: 'temp-123',
  objectives: [],
  roleDescription: '',
  objectivesDescription: '',
  behaviors: [],
};

/****************************
 * Preview Generation
 ****************************/
function assembleInstructions(d, { currentStepKey } = {}) {
  const industryLabel = INDUSTRIES.find(i => i.value === d.industry)?.label || d.otherIndustry || '';
  // Only treat an industry as selected when the draft actually contains one.
  // Previously we defaulted to 'other' which caused the generic default role to appear
  // immediately on first render. Keep industryKey null when not selected so defaults
  // aren't injected into the preview until the user makes a choice.
  const industryKey = d.industry || null;
  const tone = d.tone;
  const integrationsList = INTEGRATIONS_BY_INDUSTRY[industryKey] || [];
  const integrations = integrationsList.join(', ');

  const roleDescriptionRaw = d.roleDescription && d.roleDescription.trim();
  const objectivesDescriptionRaw = d.objectivesDescription && d.objectivesDescription.trim();
  const roleDescription = roleDescriptionRaw || (industryKey ? (DEFAULT_ROLE_DESCRIPTIONS[industryKey] || '') : '');
  const objectivesDescription = objectivesDescriptionRaw || (industryKey ? (DEFAULT_OBJECTIVES[industryKey] || '') : '');
  const toneLine = tone ? `Tone: ${tone}. ${d.brandWords ? `Brand words: ${d.brandWords}.` : ''}`.trim() : '';

  const sections = [];
  // Show industry immediately if selected
  if (industryLabel) {
    sections.push(`Industry and Expertise: ${industryLabel}`);
  }
  if (toneLine) {
    sections.push(toneLine);
  }
  if (d.behaviors && d.behaviors.length > 0) {
    sections.push(`Behavior:\n- ${d.behaviors.join('\n- ')}`);
  }
  // Include the Role section when the user has reached the Role step or any later step
  // (e.g., Objectives or Review). This keeps the role visible after it's set, but
  // prevents it from appearing on earlier steps like Industry or Tone.
  if (roleDescription && currentStepKey && KEY_TO_INDEX[currentStepKey] >= KEY_TO_INDEX['role']) {
    sections.push(`Role:\n${roleDescription}`);
  }
  if (objectivesDescriptionRaw) {
    sections.push(`Core objectives:\n${objectivesDescriptionRaw}`);
  }
  // When on the Review step, prefer the user's editable instructions if present so
  // editing in the textarea updates the preview in real-time.
  if (currentStepKey === 'review') {
    if (d.instructions && d.instructions.trim()) return d.instructions;
    // if instructions is empty, fall back to the assembled sections
  }
  return sections.join('\n\n');
}

/****************************
 * App Shell
 ****************************/
export default function App({ onComplete, isSaving = false } = {}) {
  const { currentStepKey, pushByKey } = useStepNavigation('industry');
  const step = STEPS.find(s => s.key === currentStepKey) || STEPS[0];
  const stepIndex = KEY_TO_INDEX[step.key];

  // Behavior-related state and navigation control (must be after stepIndex is available)
  const [selectedBehaviors, setSelectedBehaviors] = useState([]);
  const [selectAllBehaviors, setSelectAllBehaviors] = useState(false);
  // Track the furthest step the user has reached so we can allow jumping back to any completed step
  const [maxReachedIndex, setMaxReachedIndex] = useState(stepIndex);

  useEffect(() => {
    setMaxReachedIndex((m) => Math.max(m, stepIndex));
  }, [stepIndex]);

  const navigateToStepKey = (key) => {
    const idx = KEY_TO_INDEX[key];
    if (idx <= maxReachedIndex) {
      pushByKey(key);
    }
  };

  // Handler for individual behavior checkbox
  const onBehaviorChange = (option, checked) => {
    let updated;
    if (checked) {
      updated = [...selectedBehaviors, option];
    } else {
      updated = selectedBehaviors.filter(b => b !== option);
    }
    setSelectedBehaviors(updated);
    setSelectAllBehaviors(updated.length === BEHAVIOR_OPTIONS.length);
    setDraft(prev => ({ ...prev, behaviors: updated }));
    if (!hasInteracted) setHasInteracted(true);
  };

  // Handler for Select All checkbox
  const onSelectAllBehaviors = () => {
    if (selectAllBehaviors) {
      setSelectedBehaviors([]);
      setSelectAllBehaviors(false);
      setDraft(prev => ({ ...prev, behaviors: [] }));
    } else {
      setSelectedBehaviors([...BEHAVIOR_OPTIONS]);
      setSelectAllBehaviors(true);
      setDraft(prev => ({ ...prev, behaviors: [...BEHAVIOR_OPTIONS] }));
    }
    if (!hasInteracted) setHasInteracted(true);
  };

  const [draft, setDraft] = useState(defaultDraft);
  const [dirty, setDirty] = useState(false);

  // Live preview (debounced)
  const [preview, setPreview] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const debouncedUpdate = useMemo(() => debounce((d, stepKey) => setPreview(assembleInstructions(d, { currentStepKey: stepKey })), 150), []);
  useEffect(() => {
    // Only update preview if user has interacted
    if (hasInteracted) debouncedUpdate(draft, currentStepKey);
  }, [draft, hasInteracted, currentStepKey]);

  // Mark as interacted when user makes any change
  const onChange = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    if (!hasInteracted) setHasInteracted(true);
  };
  // Mocked server PATCH every 5s
  useEffect(() => {
    const t = setInterval(async () => {
      if (!dirty) return;
      try {
        await new Promise(res => setTimeout(res, 300));
        // mock: await fetch(`/api/agent-drafts/${draft.id}`, {method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(draft)});
        setDirty(false);
      } catch (e) {
        console.error('Auto-save error', e);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [dirty, draft]);

  // ...existing code...

  const goNext = () => {
    const nextStep = STEPS[Math.min(stepIndex + 1, STEPS.length - 1)];
    pushByKey(nextStep.key);
  };
  const goBack = () => {
    const prevStep = STEPS[Math.max(stepIndex - 1, 0)];
    pushByKey(prevStep.key);
  };

  const canContinue = useMemo(() => {
    switch (step.key) {
      case 'industry': return Boolean(draft.industry && (draft.industry !== 'other' || draft.otherIndustry));
      case 'tone': return Boolean(draft.tone);
      case 'role': return Boolean(draft.roleDescription?.trim());
      case 'objectives': return Boolean(draft.objectivesDescription?.trim());
      case 'guardrails': return Boolean(draft.guardrailsDescription?.trim());
      case 'review': return Boolean(draft.name?.trim());
      default: return true;
    }
  }, [step.key, draft]);

  return (
    <div className="builder-root" style={{ fontFamily: 'var(--font-body)' }}>
      <BrandVars />
      <div className="builder-container">
        <Progress index={stepIndex} currentStep={step} onNavigate={navigateToStepKey} maxReachedIndex={maxReachedIndex} />
        <div className="builder-grid">
          <main className="builder-card fade" aria-labelledby="stepTitle" style={{ textAlign: 'left' }}>
            {step.key === 'industry' && <IndustryScreen draft={draft} onChange={onChange} />}
            {step.key === 'tone' && <ToneScreen draft={draft} onChange={onChange} />}
            {step.key === 'behavior' && (
              <BehaviorScreen
                selectedBehaviors={selectedBehaviors}
                selectAllBehaviors={selectAllBehaviors}
                onBehaviorChange={onBehaviorChange}
                onSelectAllBehaviors={onSelectAllBehaviors}
              />
            )}
            {step.key === 'role' && <RoleScreen draft={draft} onChange={onChange} />}
            {step.key === 'objectives' && <ObjectivesScreen draft={draft} onChange={onChange} />}
            {step.key === 'review' && <ReviewScreen draft={draft} onChange={onChange} setInstructions={(v) => onChange('instructions', v)} pushByKey={pushByKey} />}
            <div className="builder-footer fade">
              <Button
                variant="outlined"
                onClick={goBack}
                disabled={stepIndex === 0}
                startIcon={<ChevronLeftIcon fontSize="small" />}
                size="small"
                sx={{ minWidth: 110, borderRadius: 2 }}
              >
                Back
              </Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (step.key === 'review') {
                      if (!draft.name?.trim() || isSaving) return;
                      if (onComplete) {
                        const payload = {
                          name: draft.name.trim(),
                          content: draft.instructions?.trim() ? draft.instructions : assembleInstructions(draft),
                        };
                        await onComplete(payload);
                      }
                    } else {
                      goNext();
                    }
                  }}
                  disabled={step.key === 'review' ? (isSaving || !draft.name?.trim()) : !canContinue}
                  endIcon={step.key === 'review' ? null : <ChevronRightIcon fontSize="small" />}
                  size="small"
                  sx={{ minWidth: 110, borderRadius: 2 }}
                >
                  {step.key === 'review' ? (isSaving ? 'Saving…' : 'Save Instruction') : 'Continue'}
                </Button>
              </div>
            </div>
          </main>
          <aside className="builder-card builder-preview-card fade" aria-live="polite" aria-atomic="true">
            <h2 className="preview-heading">Instruction Preview</h2>
            <div className="preview-box">
              {hasInteracted ? preview : ''}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export function Progress({ index, currentStep, onNavigate, maxReachedIndex }) {
  const pct = Math.min(100, Math.max(0, ((index) / (STEPS.length - 1)) * 100));
  return (
    <div className="builder-card compact progress-card fade" aria-label="Step progress">
      <div className="progress-labels">
        <div className="progress-chips">
          {STEPS.map((s) => {
            const stepIdx = KEY_TO_INDEX[s.key];
            const isClickable = stepIdx <= (typeof maxReachedIndex === 'number' ? maxReachedIndex : index); // allow navigating back to any completed step
            const isActive = s.key === currentStep.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => { if (isClickable && onNavigate) onNavigate(s.key); }}
                disabled={!isClickable}
                aria-current={isActive ? 'step' : undefined}
                className={cx('progress-step', isActive ? 'is-active' : '')}
                style={{ cursor: isClickable ? 'pointer' : 'default', background: isActive ? undefined : undefined }}
              >
                {s.name}
              </button>
            );
          })}
        </div>
        <span style={{ color: 'var(--brand-gray-500)', fontWeight: 600 }}>Step {index + 1}/{STEPS.length}</span>
      </div>
      <div className="progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/****************************
 * Screens
 ****************************/
function IndustryScreen({ draft, onChange }) {
  const [ind, setInd] = useState(draft.industry || '');
  const [other, setOther] = useState(draft.otherIndustry || '');
  useEffect(() => { onChange('industry', ind); if (ind !== 'other') { onChange('otherIndustry', undefined); } }, [ind]);
  useEffect(() => { if (ind === 'other') onChange('otherIndustry', other); }, [other, ind]);
  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">What industry is your business in?</h2>
      <div className="builder-field">
        <label htmlFor="industry">Industry</label>
        <select
          id="industry"
          className="builder-select"
          value={ind}
          onChange={e => setInd(e.target.value)}
          aria-describedby="industryHelp"
        >
          <option value="" disabled>Select an industry</option>
          {INDUSTRIES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
      {ind === 'other' && (
        <div className="builder-field">
          <label htmlFor="other">Your industry</label>
          <input
            id="other"
            className="builder-text-input"
            placeholder="e.g., Nonprofit fundraising"
            value={other}
            onChange={e => setOther(e.target.value)}
          />
        </div>
      )}
      <div style={{ marginTop: '16px', padding: '14px 16px', background: '#E9F3F7', borderRadius: '12px', fontSize: '0.9rem' }} aria-live="polite">
        You are setting up an agent for a <strong>{INDUSTRIES.find(i => i.value === ind)?.label || other || '—'}</strong> business. We&apos;ll tailor your setup accordingly.
      </div>
    </section>
  );
}

function RoleScreen({ draft, onChange }) {
  const industryKey = draft.industry || '';
  const prevIndustryRef = useRef(industryKey);

  useEffect(() => {
    const prevKey = prevIndustryRef.current;
    const previousDefault = DEFAULT_ROLE_DESCRIPTIONS[prevKey];
    const currentDefault = DEFAULT_ROLE_DESCRIPTIONS[industryKey];
    const currentDescription = (draft.roleDescription || '').trim();

    if (!industryKey) {
      prevIndustryRef.current = industryKey;
      return;
    }

    if (currentDefault) {
      if (industryKey === 'other') {
        if (currentDescription !== currentDefault) {
          onChange('roleDescription', currentDefault);
        }
      } else if (!currentDescription || currentDescription === previousDefault) {
        if (currentDescription !== currentDefault) {
          onChange('roleDescription', currentDefault);
        }
      }
    }

    prevIndustryRef.current = industryKey;
  }, [industryKey, draft.roleDescription, onChange]);

  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">Define your agent&apos;s role</h2>
      <p style={{ fontSize: '0.9rem', color: 'var(--brand-gray-500)', marginBottom: '12px' }}>
        Describe what the assistant should do, how it should respond, and when to escalate. We pre-filled guidance based on your selected industry—customize it to fit your use case.
      </p>
      <TextField
        label="Role instructions"
        value={draft.roleDescription || ''}
        onChange={(e) => onChange('roleDescription', e.target.value)}
        fullWidth
        multiline
        minRows={8}
        placeholder="Example: You are a customer support assistant..."
      />
    </section>
  );
}

function ToneScreen({ draft, onChange }) {
  const tones = ["Professional & Efficient", "Friendly & Conversational", "Empathetic & Reassuring", "Analytical & Precise", "Energetic & Persuasive", "Formal & Corporate"];
  const [words, setWords] = useState(draft.brandWords || '');
  useEffect(() => { onChange('brandWords', words); }, [words]);
  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">Personality & Tone</h2>
      <div className="tone-grid" role="radiogroup" aria-label="Tone selection">
        {tones.map(t => (
          <label key={t} className={cx("tone-tile", draft.tone === t ? "is-selected" : "")} data-active={draft.tone === t}>
            <input className="sr-only" type="radio" name="tone" value={t} checked={draft.tone === t} onChange={() => onChange('tone', t)} />
            <div style={{ fontWeight: 600 }}>{t}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--brand-gray-500)', marginTop: '6px', lineHeight: 1.4 }}>{toneHelp(t)}</div>
          </label>
        ))}
      </div>
      <div className="builder-field">
        <label htmlFor="brandWords" style={{ marginTop: '12px', display: 'block' }}>Describe your brand tone in 3 words (optional)</label>
        <input
          id="brandWords"
          className="builder-text-input"
          placeholder="e.g., friendly, clear, efficient"
          value={words}
          onChange={e => setWords(e.target.value)}
        />
      </div>
      <div style={{ marginTop: '12px', fontSize: '0.9rem' }} aria-live="polite">
        Your agent will maintain a <strong>{draft.tone || '—'}</strong> tone{draft.brandWords ? ` with ${draft.brandWords}` : ''}.
      </div>
    </section>
  );
}

function toneHelp(t) {
  switch (t) {
    case 'Professional & Efficient': return 'Clear, concise, minimal small-talk.'
    case 'Friendly & Conversational': return 'Warm phrasing; approachable; everyday language.'
    case 'Empathetic & Reassuring': return 'Acknowledges emotions; calming; supportive.'
    case 'Analytical & Precise': return 'Structured, detailed, references evidence.'
    case 'Energetic & Persuasive': return 'Upbeat, motivating, call-to-action forward.'
    case 'Formal & Corporate': return 'Polished, structured, compliant phrasing.'
    default: return ''
  }
}

function ObjectivesScreen({ draft, onChange }) {
  const industryKey = draft.industry || '';

  const prevIndustryRef = useRef(industryKey);

  useEffect(() => {
    const prevKey = prevIndustryRef.current;
    const previousDefault = DEFAULT_OBJECTIVES[prevKey];
    const currentDefault = DEFAULT_OBJECTIVES[industryKey];
    const currentDescription = (draft.objectivesDescription || '').trim();

    if (!industryKey) {
      prevIndustryRef.current = industryKey;
      return;
    }

    if (currentDefault) {
      if (industryKey === 'other') {
        if (currentDescription !== currentDefault) {
          onChange('objectivesDescription', currentDefault);
        }
      } else if (!currentDescription || currentDescription === previousDefault) {
        if (currentDescription !== currentDefault) {
          onChange('objectivesDescription', currentDefault);
        }
      }
    }

    prevIndustryRef.current = industryKey;
  }, [industryKey, draft.objectivesDescription, onChange]);

  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">Core Objectives</h2>
      <p style={{ fontSize: '0.9rem', color: 'var(--brand-gray-500)', marginBottom: '12px' }}>
        Outline the primary outcomes or goals for this assistant. We&apos;ve suggested objectives based on the selected industry—edit them to match your priorities.
      </p>
      <TextField
        label="Core objectives"
        value={draft.objectivesDescription || ''}
        onChange={(e) => onChange('objectivesDescription', e.target.value)}
        fullWidth
        multiline
        minRows={6}
        placeholder="Example: Resolve customer questions accurately and efficiently..."
      />
    </section>
  );
}


function ReviewScreen({ draft, onChange, setInstructions, pushByKey }) {
  const textRef = useRef(null);
  const [name, setName] = useState(draft.name || '');
  useEffect(() => { const initial = assembleInstructions(draft, { currentStepKey: 'review' }); setInstructions(initial); }, []);
  useEffect(() => { onChange('name', name); }, [name]);

  // Auto-generate name if not set
  useEffect(() => {
    if (!name && draft.industry) {
      const industryKey = draft.industry || 'other';
      const industryLabel = INDUSTRIES.find(i => i.value === draft.industry)?.label || draft.otherIndustry || '';
      const roleTitle = DEFAULT_ROLE_TITLES[industryKey] || 'Agent';
      if (industryLabel) {
        setName(`${roleTitle} - ${industryLabel}`);
      }
    }
  }, [draft.industry]);

  return (
    <section className="fade">
      <h2 id="stepTitle" className="builder-section-title">Review & Generate</h2>
      <div className="builder-field">
        <label htmlFor="instructionName">Instruction Name *</label>
        <input
          id="instructionName"
          className="builder-text-input"
          placeholder="e.g., Customer Support Agent - SaaS"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '18px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="editor" style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--brand-gray-900)' }}>Editable Instructions</label>
          <textarea
            id="editor"
            ref={textRef}
            className="builder-textarea"
            value={draft.instructions || ''}
            onChange={e => onChange('instructions', e.target.value)}
          />
          <div className="builder-actions" />
        </div>
      </div>
    </section>
  );
}

